import { useState } from 'react';
import type { ReactNode } from 'react';

export interface PickRow {
  key: string;
  label: string;
  /** Shown in place of the label, where the row has none to say. */
  labelNode?: ReactNode;
  meta?: string;
  glyph?: ReactNode;
  run: () => void;
  /** The row opens something in the dialog's place, so the dialog is not closed behind it. */
  keepOpen?: boolean;
}

export interface PickGroup {
  name: string;
  rows: PickRow[];
}

/**
 * The palette's sheet, for choosing one thing from what is typed: a search
 * field over named groups, ↑/↓ to move, ↵ to take the highlighted row, esc to
 * leave. The first row of the first non-empty group is what ↵ takes without
 * looking.
 */
export function PickDialog({
  label,
  placeholder,
  groups,
  hint,
  onClose,
}: {
  label: string;
  placeholder: string;
  groups: (query: string) => PickGroup[];
  hint: string;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const shown = groups(query).filter((g) => g.rows.length > 0);
  const rows = shown.flatMap((g) => g.rows);
  const active = rows[Math.min(cursor, rows.length - 1)];

  const take = (row: PickRow) => {
    row.run();
    if (!row.keepOpen) onClose();
  };

  return (
    <div className="palette-scrim" onPointerDown={onClose}>
      <div className="palette" role="dialog" aria-label={label} onPointerDown={(e) => e.stopPropagation()}>
        <div className="palette-field">
          <input
            autoFocus
            value={query}
            placeholder={placeholder}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, rows.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === 'Enter' && active) {
                e.preventDefault();
                take(active);
              }
            }}
          />
        </div>

        <div className="palette-body">
          {shown.map((group) => (
            <section key={group.name}>
              <h2 className="palette-register">{group.name}</h2>
              {group.rows.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  className={`palette-row${row.key === active?.key ? ' on' : ''}`}
                  onPointerEnter={() => setCursor(rows.findIndex((r) => r.key === row.key))}
                  onClick={() => take(row)}
                >
                  {row.glyph}
                  <span className="palette-label">{row.labelNode ?? row.label}</span>
                  {row.meta && <span className="palette-meta">{row.meta}</span>}
                </button>
              ))}
            </section>
          ))}
          {rows.length === 0 && <p className="palette-none">Nothing matches.</p>}
        </div>

        <div className="palette-foot">
          <span>{hint}</span>
        </div>
      </div>
    </div>
  );
}
